import { getServices, type Service } from '@/features/services';
import { useField, useForm } from 'vee-validate';
import { object, string } from 'zod';
import { type City, detectCity } from '@/api/cities';
import { ApiError } from '@glow/shared/lib/http';
import { toTypedSchema } from '@vee-validate/zod';

export function useHeroBlockSearch() {
  const validationSchema = toTypedSchema(
    object({
      address: string().trim().optional(),
      service: string().trim().optional(),
    })
  );
  const { handleSubmit } = useForm({ validationSchema });
  const {
    value: service,
    errorMessage: serviceError,
    setErrors: setServiceErrors,
  } = useField<string | undefined>('service');
  const {
    value: address,
    errorMessage: addressError,
    setErrors: setAddressErrors,
  } = useField<string>('address');

  const isServiceInputFocused = ref(false);
  const isAddressInputFocused = ref(false);
  const isSearching = ref(false);

  function onServiceSelect(newService: Service) {
    service.value = newService.name;
  }
  function onAddressSelect(newAddress: string) {
    address.value = newAddress;
  }
  const performSearch = handleSubmit(async (values) => {
    let city: City;
    let address: string | undefined;
    let service: Service | undefined;

    try {
      isSearching.value = true;

      try {
        city = await detectCity({ address: values.address });
        const isAddressContainsOnlyCity =
          (values.address as string)?.toLowerCase() === city.name.toLowerCase();
        address = !isAddressContainsOnlyCity ? values.address : undefined;
      } catch (e: unknown) {
        const errorText =
          e instanceof ApiError && e.statusCode === 404
            ? 'Некорректный адрес'
            : 'Неизвестная ошибка';
        setAddressErrors(errorText);
        return;
      }

      if (values.service)
        try {
          const services = await getServices(1, { name: values.service });
          service = services.data[0];
          if (!service) {
            setServiceErrors('Услуга не найдена');
            return;
          }
        } catch (e: unknown) {
          setServiceErrors('Неизвестная ошибка');
          return;
        }
    } finally {
      isSearching.value = false;
    }

    navigateTo({
      name: 'Partner catalog',
      params: {
        city: city.slug,
        specialization: service?.specialization.slug,
        service: service?.slug,
        type: 'masters',
      },
      query: { address },
    });
  });

  return {
    isServiceInputFocused,
    isAddressInputFocused,
    service,
    address,
    serviceError,
    addressError,
    onServiceSelect,
    onAddressSelect,
    performSearch,
    isSearching,
  };
}
